import React, { useMemo, useRef, useLayoutEffect, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, FormControl, FormControlLabel, MenuItem, Select } from '@mui/material';

import { getMonth, thousandFormat } from '../../../../Utils/Functions';

const ROW_HEIGHT = 23;
const MIN_NAME_WIDTH = 285;
const MAX_NAME_WIDTH = 430;
const STATUS_WIDTH = 90;
const MIN_TYPE_WIDTH = 70;
const MAX_TYPE_WIDTH = 150;

const STATUS_COLORS = {
  Converted: { active: '#8dc63f', inactive: '#dfeacf' },
  Open: { active: '#919698', inactive: '#dfe1e2' },
  Lost: { active: '#be1e2d', inactive: '#ead2d4' },
};

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function estimateTextWidth(text, charWidth = 7.2, padding = 26) {
  return Math.ceil((`${text || ''}`.length * charWidth) + padding);
}

function getTypeLabel(item) {
  return (
    item.OpportunityTypeName ||
    item.ProjectTypeCode ||
    item.ProjectTypeName ||
    item.ProjectType ||
    item.Type ||
    item.OpportunityTypeCode ||
    item.OpportunityType ||
    ''
  );
}

function useMeasuredWidth(ref) {
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    if (!ref.current) return undefined;
    const update = () => setWidth(ref.current?.offsetWidth || 0);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(ref.current);
    window.addEventListener('resize', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [ref]);

  return width;
}

function buildTicks(maxValue) {
  if (!maxValue || maxValue <= 0) return [0, 1];
  const raw = maxValue / 4;
  const magnitude = 10 ** Math.floor(Math.log10(raw || 1));
  const normalized = raw / magnitude;
  let step = magnitude;
  if (normalized > 5) step = 10 * magnitude;
  else if (normalized > 2) step = 5 * magnitude;
  else if (normalized > 1) step = 2 * magnitude;
  const ceiling = Math.ceil(maxValue / step) * step;
  const ticks = [];
  for (let i = 0; i <= 4; i += 1) ticks.push((ceiling / 4) * i);
  return ticks;
}

const BottomChart = ({
  detailData,
  selectedTopLeftItems,
  handleFilter,
  Probability,
  selectedSt,
  searchStr,
}) => {
  const wrapRef = useRef(null);
  const chartWidth = useMeasuredWidth(wrapRef);
  const [localSearchStr, setLocalSearchStr] = useState(searchStr || '');
  const [selectedKeys, setSelectedKeys] = useState([]);

  useEffect(() => { setLocalSearchStr(searchStr || ''); }, [searchStr]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if ((searchStr || '') !== localSearchStr) handleFilter({ searchStr: localSearchStr });
    }, 200);
    return () => clearTimeout(timeoutId);
  }, [localSearchStr, searchStr, handleFilter]);

  const { rows, probabilities, statuses, maxHours, nameWidth, typeWidth } = useMemo(() => {
    const monthFilters = (selectedTopLeftItems || []).map((d) => d.month);
    const probabilitySet = new Set();
    const statusSet = new Set();
    const rowMap = new Map();
    const rx = searchStr ? new RegExp(searchStr, 'i') : null;

    (detailData?.Left || []).forEach((item) => {
      const month = getMonth(item.Date);
      const probabilityValue = Number(item.ProbabilityOfSuccess || 0);
      const normalizedProbability = probabilityValue > 1 ? probabilityValue : Math.round(probabilityValue * 100);
      const statusValue = item.Status || '';
      const typeValue = getTypeLabel(item);
      const nameValue = item.Name || '';
      probabilitySet.add(normalizedProbability);
      statusSet.add(statusValue);

      const matchesMonth = monthFilters.length === 0 || monthFilters.includes(month);
      const matchesStatus = selectedSt === 'All' || !selectedSt || selectedSt === statusValue;
      const selectedProbability = Probability === 'All' ? 'All' : Number(Probability);
      const matchesProbability = selectedProbability === 'All' || selectedProbability === normalizedProbability;
      const matchesSearch = !searchStr || (rx && rx.test(nameValue));
      if (!matchesMonth || !matchesStatus || !matchesProbability || !matchesSearch) return;

      const key = `${item.OpportunityID || nameValue}||${statusValue}||${typeValue}`;
      if (!rowMap.has(key)) rowMap.set(key, { Key: key, Name: nameValue, Status: statusValue, Type: typeValue, Hours: 0 });
      rowMap.get(key).Hours += Number(item.Hours || 0);
    });

    const nextRows = Array.from(rowMap.values()).sort((a, b) => b.Hours - a.Hours);
    const dynamicNameWidth = clamp(Math.max(MIN_NAME_WIDTH, ...nextRows.map((row) => estimateTextWidth(row.Name, 7.2, 28))), MIN_NAME_WIDTH, MAX_NAME_WIDTH);
    const dynamicTypeWidth = clamp(Math.max(MIN_TYPE_WIDTH, ...nextRows.map((row) => estimateTextWidth(row.Type, 7.0, 20))), MIN_TYPE_WIDTH, MAX_TYPE_WIDTH);

    return {
      rows: nextRows,
      probabilities: Array.from(probabilitySet).sort((a, b) => a - b),
      statuses: Array.from(statusSet).sort(),
      maxHours: nextRows.reduce((max, row) => Math.max(max, row.Hours), 0),
      nameWidth: dynamicNameWidth,
      typeWidth: dynamicTypeWidth,
    };
  }, [detailData, selectedTopLeftItems, selectedSt, Probability, searchStr]);

  const ticks = useMemo(() => buildTicks(maxHours), [maxHours]);
  const chartAreaWidth = Math.max(chartWidth - nameWidth - STATUS_WIDTH - typeWidth - 24, 180);

  const getBarColor = (row) => {
    const palette = STATUS_COLORS[row.Status] || { active: '#bdbdbd', inactive: '#e8e8e8' };
    if (selectedKeys.length === 0) return palette.active;
    return selectedKeys.includes(row.Key) ? palette.active : palette.inactive;
  };

  const toggleRowSelection = (event, row) => {
    setSelectedKeys((prev) => {
      if (event.shiftKey) return prev.includes(row.Key) ? prev.filter((key) => key !== row.Key) : [...prev, row.Key];
      return prev.length === 1 && prev[0] === row.Key ? [] : [row.Key];
    });
  };

  return (
    <div style={{ height: '100%' }}>
      <div className="well" style={{ paddingTop: 30, height: '100%' }}>
        <strong style={{ fontFamily: 'Verdana', fontSize: '12px' }}>Hours By Project</strong>
        <div style={{ paddingTop: 10 }} />

        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            label="Probability"
            labelPlacement="start"
            sx={{ marginLeft: '0px !important', '& .MuiTypography-root': { fontWeight: 'bold', fontFamily: 'Verdana', fontSize: '10pt' } }}
            control={<FormControl variant="standard"><Select value={Probability} onChange={(event) => handleFilter({ Probability: event.target.value })} sx={{ '& .MuiInputBase-input': { padding: 0, paddingRight: 3, fontWeight: 'bold', fontFamily: 'Verdana', fontSize: '10pt' } }}>{[<MenuItem value="All" key="all">All</MenuItem>, ...probabilities.map((item) => <MenuItem key={`prob-${item}`} value={`${item}`}>{`${item}%`}</MenuItem>)]}</Select></FormControl>}
          />
          <FormControlLabel
            label="Status"
            labelPlacement="start"
            sx={{ marginLeft: '0px !important', '& .MuiTypography-root': { fontWeight: 'bold', fontFamily: 'Verdana', fontSize: '10pt' } }}
            control={<FormControl variant="standard"><Select value={selectedSt} onChange={(event) => handleFilter({ selectedSt: event.target.value })} sx={{ '& .MuiInputBase-input': { padding: 0, paddingRight: 3, fontWeight: 'bold', fontFamily: 'Verdana', fontSize: '10pt' } }}>{[<MenuItem value="All" key="all">All</MenuItem>, ...statuses.map((item) => <MenuItem key={`status-${item}`} value={item}>{item}</MenuItem>)]}</Select></FormControl>}
          />
          <Box display="flex" alignItems="center" gap={1}>
            <span style={{ fontWeight: 'bold', fontFamily: 'Verdana', fontSize: '12px' }}>Opportunity Name</span>
            <input type="text" name="OppName" style={{ height: 18, width: 105, paddingTop: 2 }} value={localSearchStr} onChange={(event) => setLocalSearchStr(event.target.value)} />
          </Box>
        </Box>

        <div ref={wrapRef} id="ol_bottom" style={{ maxHeight: '36vh', overflowY: 'auto', overflowX: 'hidden', borderTop: 'solid 1px #a9a9a9', width: '100%' }}>
          {rows.map((row) => {
            const width = maxHours > 0 ? Math.min(Math.max((row.Hours / maxHours) * chartAreaWidth, row.Hours > 0 ? 2 : 0), chartAreaWidth - 2) : 0;
            const selected = selectedKeys.includes(row.Key);
            return (
              <div key={row.Key} onClick={(event) => toggleRowSelection(event, row)} title={`${row.Name} | ${row.Status} | Hours: ${thousandFormat(row.Hours)}`} style={{ display: 'flex', alignItems: 'center', height: ROW_HEIGHT, userSelect: 'none', borderLeft: 'solid 1px #a9a9a9', borderRight: 'solid 1px #a9a9a9', borderBottom: 'solid 1px #a9a9a9', cursor: 'pointer', backgroundColor: selected ? '#f7f7f7' : 'transparent' }}>
                <div style={{ width: nameWidth, paddingLeft: 2, paddingRight: 6, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.Name}</div>
                <div style={{ width: STATUS_WIDTH, paddingLeft: 1, paddingRight: 6, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.Status}</div>
                <div style={{ width: typeWidth, borderRight: 'solid 1px #a9a9a9', paddingLeft: 1, paddingRight: 6, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.Type}</div>
                <div style={{ flex: 1, height: '100%', position: 'relative' }}><div style={{ position: 'absolute', left: 0, top: 3, height: 18, width, backgroundColor: getBarColor(row) }} /></div>
              </div>
            );
          })}
        </div>

        {rows.length > 0 && (
          <div style={{ width: '100%', paddingTop: 2 }}>
            <div style={{ display: 'flex', width: '100%' }}>
              <div style={{ width: nameWidth + STATUS_WIDTH + typeWidth + 1 }} />
              <div style={{ flex: 1, position: 'relative', height: 20, overflow: 'visible' }}>
                {ticks.map((tick, idx) => {
                  const axisMax = ticks[ticks.length - 1] || maxHours || 1;
                  const left = axisMax > 0 ? (tick / axisMax) * chartAreaWidth : 0;
                  const transform = idx === 0 ? 'translateX(0)' : idx === ticks.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)';
                  const textAlign = idx === 0 ? 'left' : idx === ticks.length - 1 ? 'right' : 'center';
                  return <div key={`tick-${tick}-${idx}`} style={{ position: 'absolute', left, top: 0, transform, fontSize: 11, textAlign, whiteSpace: 'nowrap' }}><div style={{ borderLeft: '1px solid black', height: 5, margin: idx === 0 ? '0' : idx === ticks.length - 1 ? '0 0 0 auto' : '0 auto' }} /><div>{thousandFormat(tick)}</div></div>;
                })}
              </div>
            </div>
          </div>
        )}

        <Box display="flex" alignItems="center" gap={2} pt={1}>
          {Object.entries(STATUS_COLORS).map(([label, palette]) => (
            <Box key={label} display="flex" alignItems="center" gap={0.5}>
              <div style={{ width: 10, height: 10, background: palette.active }} />
              <span style={{ fontSize: 12 }}>{label}</span>
            </Box>
          ))}
        </Box>
      </div>
    </div>
  );
};

BottomChart.propTypes = {
  detailData: PropTypes.object,
  selectedTopLeftItems: PropTypes.array,
  handleFilter: PropTypes.func.isRequired,
  Probability: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selectedSt: PropTypes.string,
  searchStr: PropTypes.string,
};

BottomChart.defaultProps = {
  detailData: null,
  selectedTopLeftItems: [],
  Probability: 'All',
  selectedSt: 'All',
  searchStr: '',
};

export default BottomChart;
